import React, { useState, useEffect } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { NavBar } from './components/NavBar/NavBar.jsx';
import { Route, Routes } from 'react-router-dom';
import Account from './pages/Account.jsx';
import Home from './pages/Home.jsx';
import { isMobile } from 'react-device-detect';
import Preferences from './pages/Preferences.jsx';
import Configuration from './pages/Configuration.jsx';
import TicketTracking from './pages/TicketTracking.jsx';
import Reports from './pages/Reports.jsx';
import SightTestHistory from './pages/SightTestHistory.jsx';
import Clinics from './pages/Clinics.jsx';
import BusinessOpportunities from './pages/BusinessOpportunities.jsx';
import OpticianClinics from './pages/OpticiansClinics.jsx';
import { fetchTheme } from './http/account.js';
import { getCookie } from './http/auth.js';
import { fetchAccount } from './http/account.js';
import PrivateOrders from './pages/PrivateOrders.jsx';
import EyeTicketsAwaitingProcessing from './pages/EyeTicketsAwaitingProcessing.jsx';
import { fetchDistributorAccount } from './http/distributor.js';
import AuthoriseEyeTickets from './pages/AuthoriseEyeTickets.jsx';
import ScrollToTop from './components/Helpers/ScrollToTop.jsx';
import { distributors, opticians, endUsers } from './components/Helpers/userTypes.js';


function App() {
  const cachedTheme = JSON.parse(localStorage.getItem('theme'));
  const [navBarHeight, setNavBarHeight] = useState('0px');
  const [accountType, setAccountType] = useState(getCookie('accountType', false));
  const [backgroundColour, setBackgroundColour] = useState(
    cachedTheme || {
      sidebar: '#333A58',
      menuContent: '#2D344F',
      hoverColour: '#585F7C',
      font: '#BAC3CB',
      appName: 'MCR Eye Ticket',
      image: '',
      isDefault: true,
      themeId: 0,
    }
  );

  const [error, setError] = useState(null);
  const [account, setAccount] = useState(null);
  const navigate = useNavigate();

  function setDefaultColourScheme(){
    setBackgroundColour({
      sidebar: '#333A58',
      menuContent: '#2D344F',
      hoverColour: '#585F7C',
      font: '#BAC3CB',
      appName: backgroundColour.appName,
      image: backgroundColour.image,
      isDefault: backgroundColour.isDefault,
      themeId: backgroundColour.themeId
    })
  }

  const handleImageUpload = (base64Image) => {
    setBackgroundColour(prev => ({ ...prev, image: base64Image }));
  };

  const handleAppNameChange = (applicationName) => {
    setBackgroundColour(prev => ({ ...prev, appName: applicationName }));
  };

  const setCookie = (name, value, expiration) => {
    const expires = new Date(expiration).toUTCString();
    document.cookie = `${name}=${value}; expires=${expires}; path=/; SameSite=Strict`;
  };

  const userEmail = getCookie('username', false);
  const cCustId = 'TEST004';

  useEffect(() => {
    const loadTheme = async () => {
      try {
        const fetchedTheme = await fetchTheme(getCookie('username', false));
        if (fetchedTheme[0]) {
          const tmpBackgroundColor = {
            sidebar: fetchedTheme[0].Sidebar,
            menuContent: fetchedTheme[0].MenuContent,
            hoverColour: fetchedTheme[0].Hover,
            font: fetchedTheme[0].Font,
            appName: fetchedTheme[0].ApplicationName,
            image: fetchedTheme[0].logo_imageBin,
            isDefault: false,
            themeId: fetchedTheme[0].ct_unqID,
          };
          setBackgroundColour(tmpBackgroundColor);

          localStorage.setItem('theme', JSON.stringify(tmpBackgroundColor));
        }
        setError(null);
      } catch (e) {
        setError(e);
      }
    };

    const loadAccount = async () => {
      try {
        if (distributors.includes(accountType)) {
          const fetchedAccount = await fetchDistributorAccount();
          setAccount(fetchedAccount[0]);
        } else {
          const fetchedAccount = await fetchAccount(userEmail);
          setAccount(fetchedAccount.Users_Profile);
        }
        setError(null);
      } catch (e) {
        setError(e);
      }
    };

    if (isMobile) {
      setNavBarHeight('60px');
    }

    loadTheme();
    loadAccount();
  }, [userEmail]);

  useEffect(() => {
    //const token = getCookie('token');
    const token = getCookie('username');

    if (!token) {
      navigate('/login?expired=true');
    }
  }, [navigate]);

  return (
    <div className="App" style={{ display: 'flex', minHeight: '100vh' }}>
      <NavBar
        backgroundColor={backgroundColour}
        accountType={accountType}
        userEmail={userEmail}
        account={account}
      />
      <main style={{ flexGrow: 1, paddingTop: navBarHeight }}>
        <div style={{ padding: '16px 24px', color: '#44596e' }}>
        <ScrollToTop />
          <Routes>
            <Route
              path="/account"
              element={
                <Account
                  email={userEmail}
                  colour={backgroundColour}
                  handleImageUpload={handleImageUpload}
                />
              }
            />
            <Route
              path="/preferences"
              element={
                <Preferences
                  email={userEmail}
                  colour={backgroundColour}
                  setSidebar={setBackgroundColour}
                  setAppName={(newAppName) =>
                    setBackgroundColour((prev) => ({ ...prev, appName: newAppName }))
                  }
                  accountType={accountType}
                  setDefaultTheme={setDefaultColourScheme}
                />
              }
            />
            <Route
              path="/configuration"
              element={<Configuration email={userEmail} colour={backgroundColour} accountType={accountType} />}
            />
            <Route
              path="/tracking"
              element={<TicketTracking colour={backgroundColour} email={userEmail} tempCCustId={cCustId} accountType={accountType} />}
            />
            <Route path="/reports" element={<Reports colour={backgroundColour} email={userEmail} />} />
            <Route path="/authorisetickets" element={<AuthoriseEyeTickets colour={backgroundColour} email={userEmail} />} />
            <Route path="/clinics" element={<Clinics colour={backgroundColour} email={userEmail} />} />
            <Route path="/opticianclinics" element={<OpticianClinics colour={backgroundColour} />} />
            <Route path="/testhistory" element={<SightTestHistory colour={backgroundColour} />} />
            <Route path="/privateorders" element={<PrivateOrders colour={backgroundColour} />} />
            <Route path="/awaitingprocessing" element={<EyeTicketsAwaitingProcessing colour={backgroundColour} />} />
            <Route path="/businessopportunities" element={<BusinessOpportunities colour={backgroundColour} />} />
            <Route path="/home" element={<Home colour={backgroundColour} accountType={accountType} />} />
            <Route path="/" element={<Navigate to="/home" />} />
          </Routes>
        </div>
      </main>
    </div>
  );
}

export default App;
